import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Save,
  Building2,
  MapPin,
  Users,
  Settings,
  CheckCircle,
  AlertCircle,
  Calendar,
  Bot,
  Database,
  Activity,
  Clock,
  Hash,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  X,
  ArrowLeft,
} from 'lucide-react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { GooglePlacesAutocomplete, AddressDetails } from '@/components/GooglePlacesAutocomplete'
import { SearchableEntitySelect } from '@/components/SearchableEntitySelect'

// Typology options as specified by user
const TYPOLOGY_OPTIONS = [
  'Apartment',
  'House',
  'Apartment & House',
  'Tourist Apartment'
] as const

// Validation schema
const projectSchema = z.object({
  name: z.string()
    .min(1, 'Project name is required')
    .max(255, 'Project name must be less than 255 characters')
    .regex(/^[\p{L}\p{N}\s\-_.,()&]+$/u, 'Project name contains invalid characters'),
  website: z.string()
    .min(1, 'Website is required')
    .url('Please enter a valid URL (e.g., https://example.com)')
    .refine((val) => val.startsWith('http://') || val.startsWith('https://'),
      'Website must start with http:// or https://'),
  address: z.string()
    .max(500, 'Address must be less than 500 characters')
    .optional(),
  city: z.string()
    .max(100, 'City name is too long')
    .optional(),
  district: z.string()
    .max(100, 'District name is too long')
    .optional(),
  status: z.enum(['active', 'sold_out', 'pending', 'cancelled'], {
    message: 'Please select a valid project status'
  }),
  architect_id: z.number()
    .int('Architect ID must be a whole number')
    .positive('Architect ID must be positive')
    .optional(),
  developer_id: z.number()
    .int('Developer ID must be a whole number')
    .positive('Developer ID must be positive')
    .optional(),
  broker_id: z.number()
    .int('Broker ID must be a whole number')
    .positive('Broker ID must be positive')
    .optional(),
  typology: z.string()
    .max(100, 'Typology description is too long')
    .optional(),
  full_description: z.string()
    .max(5000, 'Description must be less than 5000 characters')
    .optional(),
  area: z.string()
    .max(100, 'Area name is too long')
    .optional(),
  neighbourhood: z.string()
    .max(100, 'Neighborhood name is too long')
    .optional(),
  listing_date: z.string()
    .optional()
    .refine((val) => !val || !isNaN(Date.parse(val)), 'Please enter a valid date'),
  has_parking: z.boolean().optional(),
  has_pool: z.boolean().optional(),
  has_gym: z.boolean().optional(),
  sold_out: z.boolean().optional(),
})

type ProjectFormData = z.infer<typeof projectSchema>

export function CreateProject() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      status: 'active',
      has_parking: false,
      has_pool: false,
      has_gym: false,
      sold_out: false,
    },
    mode: 'onChange',
  })

  // Watch form values for real-time feedback
  const watchedValues = watch()
  


  // Helper function to get input field class names
  const getInputClassName = (fieldName: keyof ProjectFormData, hasError: boolean = false) => {
    const baseClass = "form-input"
    if (hasError) return `${baseClass} border-danger-300 focus:border-danger-500 focus:ring-danger-500`
    if (isDirty && !hasError) return `${baseClass} border-success-300 focus:border-success-500 focus:ring-success-500`
    return baseClass
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ProjectFormData) => {
      const response = await api.post('/projects', data)
      return response.data
    },
    onSuccess: (data) => {
      toast.success('Project created successfully!')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects')
    },
    onError: (error: any) => {
      console.error('Error creating project:', error)
      console.error('Error response:', error.response)
      console.error('Error data:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error message:', error.response?.data?.message)
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to create project'
      
      toast.error(`API Error: ${errorMessage}`)
    },
  })

  const onSubmit = (data: ProjectFormData) => {
    createMutation.mutate(data)
  }

  const cleanFormData = (data: any) => {
    const cleaned = { ...data }
    
    // Remove undefined values for optional fields
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === undefined || cleaned[key] === '' || cleaned[key] === null) {
        delete cleaned[key]
      }
    })
    
    // Convert empty strings to undefined for number fields
    if (cleaned.architect_id === '' || cleaned.architect_id === null) {
      delete cleaned.architect_id
    }
    if (cleaned.developer_id === '' || cleaned.developer_id === null) {
      delete cleaned.developer_id
    }
    if (cleaned.broker_id === '' || cleaned.broker_id === null) {
      delete cleaned.broker_id
    }
    
    return cleaned
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Trigger validation for all fields
    const isFormValid = await trigger()
    
    if (isFormValid) {
      const formData = watch()
      const cleanedData = cleanFormData(formData)
      createMutation.mutate(cleanedData)
    } else {
      toast.error('Please fix the form errors before submitting')
    }
  }


  const isLoading = createMutation.isPending

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 -ml-2 rounded-lg text-secondary-500 hover:text-secondary-900 hover:bg-secondary-100 transition-colors"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Create New Project</h1>
            <p className="text-secondary-500 text-sm mt-1">Add a new real estate project to the system</p>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-tagus p-8">
          <form onSubmit={handleFormSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-primary-600" />
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="project-name" className="form-label">
                    Project Name <span className="text-danger-500">*</span>
                  </label>
                  <input
                    {...register('name')}
                    id="project-name"
                    name="name"
                    type="text"
                    className={getInputClassName('name', !!errors.name)}
                    placeholder="Enter project name"
                  />
                  {errors.name && (
                    <p className="text-danger-500 text-sm flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="project-website" className="form-label">
                    Website <span className="text-danger-500">*</span>
                  </label>
                  <input
                    {...register('website')}
                    id="project-website"
                    name="website"
                    type="url"
                    className={getInputClassName('website', !!errors.website)}
                    placeholder="https://example.com"
                  />
                  {errors.website && (
                    <p className="text-danger-500 text-sm flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.website.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label htmlFor="project-status" className="form-label">Status</label>
                  <select
                    {...register('status')}
                    id="project-status"
                    name="status"
                    className={getInputClassName('status', !!errors.status)}
                  >
                    <option value="active">Active</option>
                    <option value="sold_out">Sold Out</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {errors.status && (
                    <p className="text-danger-500 text-sm flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.status.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="project-typology" className="form-label">Typology</label>
                  <select
                    {...register('typology')}
                    id="project-typology"
                    name="typology"
                    className={getInputClassName('typology', !!errors.typology)}
                  >
                    <option value="">Select typology</option>
                    {TYPOLOGY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="project-listing-date" className="form-label">Launch Date</label>
                  <input
                    {...register('listing_date')}
                    id="project-listing-date"
                    name="listing_date"
                    type="date"
                    className={getInputClassName('listing_date', !!errors.listing_date)}
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-primary-600" />
                Location Information
              </h3>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="project-address" className="form-label">Address</label>
                  <GooglePlacesAutocomplete
                    value={watchedValues.address || ''}
                    onChange={(value) => setValue('address', value)}
                    onAddressSelect={(details: AddressDetails) => {
                      // Auto-fill city and district if available and empty
                      if (details.city && !watchedValues.city) {
                        setValue('city', details.city)
                      }
                      if (details.district && !watchedValues.district) {
                        setValue('district', details.district)
                      }
                    }}
                    placeholder="Start typing address (e.g., Rua de Belém 1, Lisbon)..."
                    error={errors.address?.message}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="project-city" className="form-label">City</label>
                    <input
                      {...register('city')}
                      id="project-city"
                      name="city"
                      type="text"
                      className={getInputClassName('city', !!errors.city)}
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="project-district" className="form-label">District</label>
                    <input
                      {...register('district')}
                      id="project-district"
                      name="district"
                      type="text"
                      className={getInputClassName('district', !!errors.district)}
                      placeholder="Enter district"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="project-area" className="form-label">Area</label>
                    <input
                      {...register('area')}
                      id="project-area"
                      name="area"
                      type="text"
                      className={getInputClassName('area', !!errors.area)}
                      placeholder="Enter area"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="project-neighbourhood" className="form-label">Neighborhood</label>
                  <input
                    {...register('neighbourhood')}
                    id="project-neighbourhood"
                    name="neighbourhood"
                    type="text"
                    className={getInputClassName('neighbourhood', !!errors.neighbourhood)}
                    placeholder="Enter neighborhood"
                  />
                </div>
              </div>
            </div>

            {/* Team Information */}
            <div className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SearchableEntitySelect
                  label="Architect"
                  value={watchedValues.architect_id}
                  onChange={(value) => setValue('architect_id', value)}
                  fetchUrl="/architects"
                  placeholder="Search architects..."
                  entityType="architect"
                />

                <SearchableEntitySelect
                  label="Developer"
                  value={watchedValues.developer_id}
                  onChange={(value) => setValue('developer_id', value)}
                  fetchUrl="/developers"
                  placeholder="Search developers..."
                  entityType="developer"
                />

                <SearchableEntitySelect
                  label="Broker"
                  value={watchedValues.broker_id}
                  onChange={(value) => setValue('broker_id', value)}
                  fetchUrl="/brokers"
                  placeholder="Search brokers..."
                  entityType="broker"
                />
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-primary-600" />
                Project Details
              </h3>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="project-description" className="form-label">Description</label>
                  <textarea
                    {...register('full_description')}
                    id="project-description"
                    name="full_description"
                    rows={4}
                    className={getInputClassName('full_description', !!errors.full_description)}
                    placeholder="Enter project description"
                  />
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-primary-600" />
                Amenities
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <label htmlFor="project-parking" className="flex items-center space-x-2 cursor-pointer">
                  <input
                    {...register('has_parking')}
                    id="project-parking"
                    name="has_parking"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <span className="text-sm text-secondary-700">Parking</span>
                </label>

                <label htmlFor="project-pool" className="flex items-center space-x-2 cursor-pointer">
                  <input
                    {...register('has_pool')}
                    id="project-pool"
                    name="has_pool"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <span className="text-sm text-secondary-700">Pool</span>
                </label>

                <label htmlFor="project-gym" className="flex items-center space-x-2 cursor-pointer">
                  <input
                    {...register('has_gym')}
                    id="project-gym"
                    name="has_gym"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <span className="text-sm text-secondary-700">Gym</span>
                </label>

                <label htmlFor="project-sold-out" className="flex items-center space-x-2 cursor-pointer">
                  <input
                    {...register('sold_out')}
                    id="project-sold-out"
                    name="sold_out"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <span className="text-sm text-secondary-700">Sold Out</span>
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-8 border-t border-secondary-200">
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="btn-secondary flex items-center"
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                className={`btn-primary flex items-center ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span className="animate-pulse">Creating...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Project
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
